import { useMemo } from 'react';
import { Divider } from '@mui/material';
import {
  BooleanField,
  FormTab,
  FormTabProps,
  ReferenceField,
  TextField,
  required,
  useGetOne,
  useNotify,
  useRecordContext,
} from 'react-admin';
import { isEqual } from 'lodash';

import { CustomerDataPackageRecord, DataPackageTemplateRecord } from 'src/types';
import { CraftPageSection } from 'src/components/CraftPageSection';
import CraftTwoColumns from 'src/components/CraftTwoColumns';
import Labeled from 'src/components/Labeled';
import TextInput from 'src/inputs/TextInput';
import tooltips from '../tooltips';
import { getFieldComparisonDataMap } from 'src/utils/apiFieldsConfig';

const DetailsTab = (props: Omit<FormTabProps, 'label'>) => {
  const record = useRecordContext<CustomerDataPackageRecord>();
  const notify = useNotify();

  const {
    data: dataPackageTemplateData,
    isLoading: dataPackageTemplateLoading,
    error: dataPackageTemplateError,
  } = useGetOne<DataPackageTemplateRecord>('data_package_templates', { id: record.data_package_template_id });

  const isCustomDataPackage = useMemo(() => {
    if (!dataPackageTemplateData) return undefined;
    return !isEqual(
      getFieldComparisonDataMap(record.api_fields_xrefs),
      getFieldComparisonDataMap(dataPackageTemplateData.api_fields_xrefs),
    );
  }, [dataPackageTemplateData]);

  if (dataPackageTemplateError) {
    notify('There was a problem loading data package template data. Please refresh to try again.', {
      type: 'error',
    });
  }

  return (
    <FormTab label="Details" {...props}>
      <CraftPageSection title="General" loading={dataPackageTemplateLoading}>
        <CraftTwoColumns left={<TextInput source="name" validate={required()} fullWidth />} right={null} />
        <Divider />
        <CraftTwoColumns
          mt={2}
          rowSpacing={1}
          columnSpacing={4}
          left={
            <ReferenceField
              sx={{ '& > .RaReferenceField-link': { display: 'flex' } }}
              source="data_package_template_id"
              reference="data_package_templates"
              link="show"
            >
              <Labeled label="Template">
                <TextField source="name" />
              </Labeled>
            </ReferenceField>
          }
          right={
            <Labeled label="Custom" tooltip={tooltips.customIndicator}>
              <BooleanField record={{ isCustom: isCustomDataPackage }} source="isCustom" />
            </Labeled>
          }
        />
      </CraftPageSection>
    </FormTab>
  );
};

export default DetailsTab;
